import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/components/ui/use-toast";

/** Loads a review detail payload while `open`, and exposes a refresh for after saves. */
export function useReviewDetail<T>(open: boolean, load: () => Promise<T>) {
  const { toast } = useToast();
  const [data, setData] = useState<T | null>(null);
  // Callers rebuild `load` every render; refs keep `refresh` stable so the effect runs once per open.
  const loadRef = useRef(load);
  const toastRef = useRef(toast);
  loadRef.current = load;
  toastRef.current = toast;

  const refresh = useCallback(async () => {
    try {
      setData(await loadRef.current());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toastRef.current({
        title: "Failed to load detail",
        description: message,
        variant: "destructive",
      });
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  return { data, refresh };
}
